/**
 * Turn a backend `permission_denied` into structured, role-aware copy.
 *
 * With hide-by-default gating (see permissions.js), most denied
 * affordances never render. This is the fallback for the denials that
 * CAN'T be pre-hidden: a sensitive-column read the role lacks, or a
 * permission that changed mid-session. The customer must never see a
 * raw `ExecuteQuery 403: {...}` string.
 *
 * Scope refusals use the closed `abac.v1.ScopeDenialDetail` carried in
 * `google.rpc.Status.details`. That type is independent from the richer
 * audit enum, so the browser cannot receive a row-existence or verifier
 * reason by accident. Untyped RBAC denials branch only on gRPC code 7 and
 * receive generic role-aware copy. Backend English is display text, never
 * a control-flow contract.
 */
import { roleNames } from "./permissions.js";

// Reason codes the dashboard acts on. `permission_denied` is a code-7 we
// don't classify further (the catch-all role-aware denial);  `unknown`
// is a non-permission error (5xx, parse failure) that must NOT be
// dressed up as a permissions problem.
export const DENIAL_REASON = Object.freeze({
    PERMISSION_DENIED: "permission_denied",
    ESCALATION_ENDED: "escalation_ended",
    ROW_SCOPE_UNENFORCEABLE: "row_scope_unenforceable",
    SCOPE_EMPTY: "scope_empty",
    VIEWER_UNMAPPED: "viewer_unmapped",
    SCOPE_UNAVAILABLE: "scope_unavailable",
    UNKNOWN: "unknown",
});

const SCOPE_DETAIL_TYPE = "type.googleapis.com/abac.v1.ScopeDenialDetail";
const CLIENT_SCOPE_REASON = Object.freeze({
    CLIENT_SCOPE_DENIAL_REASON_ROW_SCOPE_UNENFORCEABLE: DENIAL_REASON.ROW_SCOPE_UNENFORCEABLE,
    CLIENT_SCOPE_DENIAL_REASON_SCOPE_EMPTY: DENIAL_REASON.SCOPE_EMPTY,
    CLIENT_SCOPE_DENIAL_REASON_VIEWER_UNMAPPED: DENIAL_REASON.VIEWER_UNMAPPED,
    CLIENT_SCOPE_DENIAL_REASON_SCOPE_UNAVAILABLE: DENIAL_REASON.SCOPE_UNAVAILABLE,
    1: DENIAL_REASON.ROW_SCOPE_UNENFORCEABLE,
    2: DENIAL_REASON.SCOPE_EMPTY,
    3: DENIAL_REASON.VIEWER_UNMAPPED,
    4: DENIAL_REASON.SCOPE_UNAVAILABLE,
});

// Pull the gRPC status message out of whatever the caller hands us: a
// thrown Error ("ExecuteQuery 403: {json}"), a raw response body
// (`{"code":7,"message":"…"}`), or a bare message string. Returns
// { code, message }.
function extractStatus(input) {
    if (input == null) return { code: null, message: "", details: [] };
    if (typeof input === "object" && Array.isArray(input.grpcDetails)) {
        return {
            code: typeof input.grpcStatusCode === "number" ? input.grpcStatusCode : null,
            message: input.serverMessage ?? input.message ?? "",
            details: input.grpcDetails,
        };
    }
    const text = typeof input === "string" ? input : (input.message ?? String(input));
    // Only treat a brace as the status envelope when the text IS a JSON
    // object or carries the gateway's "<label> NNN: {json}" prefix — don't
    // grab an arbitrary brace from inside a prose message. The label can
    // contain a colon-verb REST path (e.g. "POST /v1/skills:publish-version
    // 403: ..."), so anchor on the ` <3-digit-status>: ` delimiter
    // (greedy prefix), not on a colon-free run.
    const enveloped = text.match(/^(?:.*\s\d{3}:\s)?(\{[\s\S]*\})\s*$/);
    if (enveloped) {
        try {
            const obj = JSON.parse(enveloped[1]);
            if (obj && (typeof obj.message === "string" || typeof obj.code === "number")) {
                return {
                    code: typeof obj.code === "number" ? obj.code : null,
                    message: obj.message ?? "",
                    details: Array.isArray(obj.details) ? obj.details : [],
                };
            }
        } catch {
            // fall through to raw-text handling
        }
    }
    // No JSON envelope: strip a leading "<label> NNN: " status prefix if present.
    const m = text.match(/^.*\s\d{3}:\s([\s\S]*)$/);
    return { code: null, message: m ? m[1] : text, details: [] };
}

function typedScopeReason(details) {
    const detail = (Array.isArray(details) ? details : []).find(
        (entry) => entry && (entry["@type"] === SCOPE_DETAIL_TYPE || entry.typeUrl === SCOPE_DETAIL_TYPE),
    );
    return detail ? CLIENT_SCOPE_REASON[detail.reason] ?? null : null;
}

// Classify a backend denial. Returns a structured shape the UI renders
// from - never the raw string. Only typed details and numeric gRPC codes
// affect classification; message prose is never parsed.
export function parseDenial(input) {
    const { code, message, details } = extractStatus(input);
    const base = { raw: message, requiredPermission: null, grpcCode: code };

    const scopeReason = typedScopeReason(details);
    if (scopeReason) return { ...base, reason: scopeReason };

    // Any untyped PERMISSION_DENIED gets the generic role-aware denial. A
    // non-7 (5xx/network) is UNKNOWN and must not be framed as permissions.
    if (code === 7) return { ...base, reason: DENIAL_REASON.PERMISSION_DENIED };
    return { ...base, reason: DENIAL_REASON.UNKNOWN };
}

// "You're signed in as SecOps" / "as SecOps and Viewer" / "Your
// account" when the role set is unknown.
export function roleClause(caps) {
    const roles = roleNames(caps);
    if (!roles.length) return "Your account";
    if (roles.length === 1) return `You're signed in as ${roles[0]}`;
    if (roles.length === 2) return `You're signed in as ${roles[0]} and ${roles[1]}`;
    return `You're signed in as ${roles.slice(0, -1).join(", ")}, and ${roles[roles.length - 1]}`;
}

// The posture is cached for up to 30 seconds, so a window the server has
// already stopped honouring is still listed on it — which is exactly the state
// a mid-investigation denial arrives in. A caller still holding a live window
// keeps the generic copy, because that window is not what denied them.
function endedEscalation(caps) {
    const windows = Array.isArray(caps?.activeEscalations) ? caps.activeEscalations : [];
    const now = Date.now();
    const lapsed = windows.filter((e) => e.expiresAt && e.expiresAt.getTime() <= now);
    if (!lapsed.length || lapsed.length !== windows.length) return null;
    return lapsed.reduce((latest, e) => (e.expiresAt > latest.expiresAt ? e : latest));
}

const endedAtFormatter = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });

// Role-aware, customer-facing copy for a denial. Returns
// { reason, message, requiredPermission } — the UI renders `message`.
export function denialCopy(input, caps) {
    const parsed = input && input.reason ? input : parseDenial(input);
    const clause = roleClause(caps);
    let message;

    if (parsed.reason === DENIAL_REASON.PERMISSION_DENIED) {
        const ended = endedEscalation(caps);
        if (ended) {
            return {
                reason: DENIAL_REASON.ESCALATION_ENDED,
                message: `Your temporary access ended at ${endedAtFormatter.format(ended.expiresAt)}. Request it again if the investigation is still open.`,
                requiredPermission: parsed.requiredPermission,
            };
        }
    }

    switch (parsed.reason) {
        case DENIAL_REASON.PERMISSION_DENIED:
            message = `${clause}, which doesn't have permission to do this.`;
            break;
        case DENIAL_REASON.ESCALATION_ENDED:
            message = "Your temporary access has ended. Request it again if the investigation is still open.";
            break;
        case DENIAL_REASON.ROW_SCOPE_UNENFORCEABLE:
            message = "This view cannot safely narrow its data to the people you can see, so it is unavailable for your account.";
            break;
        case DENIAL_REASON.SCOPE_EMPTY:
            message = "Your account cannot see anyone's activity. Ask an access administrator to check your grants.";
            break;
        case DENIAL_REASON.VIEWER_UNMAPPED:
            message = "Your account is not linked to a directory person. Ask an access administrator to fix that mapping; widening a grant will not fix it.";
            break;
        case DENIAL_REASON.SCOPE_UNAVAILABLE:
            message = "Your activity scope is temporarily unavailable. Please try again.";
            break;
        default:
            // Not a permissions problem — don't pretend it is.
            message = "Something went wrong. Please try again.";
    }

    return {
        reason: parsed.reason,
        message,
        requiredPermission: parsed.requiredPermission,
    };
}

// For REST action handlers: render role-aware copy when the failure is an
// actual permission denial, but PRESERVE the backend's own message for
// anything else (a 4xx validation rejection, a conflict, a network
// error). Using denialCopy unconditionally would collapse those into the
// generic "Something went wrong", masking actionable detail like
// "max_uses out of range". Returns the clean inner message (extractStatus
// unwraps the `{code,message}` envelope) when present.
export function denialOrRawMessage(err, caps) {
    const parsed = parseDenial(err);
    if (parsed.reason !== DENIAL_REASON.UNKNOWN) return denialCopy(parsed, caps).message;
    return parsed.raw || (err && err.message) || String(err);
}
